const SELECTORS = {
  chip: "[data-fo-chip]",
  card: "[data-fo-card]",
  grid: "[data-fo-grid]",
  emptyState: "[data-fo-empty]",
  pagination: "[data-fo-pagination]",
  pageButton: "[data-fo-page]",
  hasNext: "[data-fo-has-next]",
};

const CATEGORY_PARAM = "kategorie";
// Anzeige-Seite bewusst NICHT `page` nennen — das ist der Liquid-`paginate`-Param
// und würde beim Reload den Section-Chunk statt der Anzeige-Seite steuern.
const PAGE_PARAM = "seite";
const DEFAULT_PAGE_SIZE = 50;
// Sicherung gegen Endlosschleife beim Akkumulieren (250 * 40 = 10.000 Freebooks).
const MAX_FETCH_PAGES = 40;

// Optik-Klassen aus `snippets/pagination.liquid` (Size `md`, Shape `full`,
// Active-Style `dashed`) gespiegelt, damit die JS-Controls identisch aussehen.
const CLASS = {
  square: "h-8 w-8 sm:h-11 sm:w-11 rounded-full",
  pill: "h-8 min-w-8 px-2 text-xs sm:h-11 sm:min-w-11 sm:text-sm rounded-full",
  link: "inline-flex items-center justify-center border border-transparent text-(--color-text) hover:bg-(--color-text)/5",
  disabled:
    "inline-flex cursor-default items-center justify-center border border-transparent text-(--color-text)/30",
  active:
    "inline-flex items-center justify-center px-2 font-semibold text-(--color-text) border border-dashed border-(--pagination-accent) bg-(--pagination-accent)/20",
  gap: "inline-flex items-center justify-center px-2 text-xs text-(--color-text)/50 sm:text-sm",
};

class FreebookOverview extends HTMLElement {
  #chips = [];
  #cards = []; // vollständiger, akkumulierter Satz = einzige Wahrheitsquelle
  #grid = null;
  #emptyState = null;
  #pagination = null;
  #activeHandle = "";
  #currentPage = 1;
  #pageSize = DEFAULT_PAGE_SIZE;
  #sectionId = "";
  #labels = { previous: "", next: "" };

  connectedCallback() {
    try {
      this.#init();
    } catch (error) {
      console.error("[FreebookOverview:init]", error);
    }
  }

  #init() {
    this.#bindElements();
    this.#bindEvents();

    const params = new URLSearchParams(window.location.search);
    this.#activeHandle = params.get(CATEGORY_PARAM) || "";
    this.#currentPage = Math.max(1, parseInt(params.get(PAGE_PARAM), 10) || 1);

    this.#render({ updateUrl: false });
    // Bei <=250 Freebooks fehlt der Marker -> kein einziger Extra-Request.
    this.#loadRemainingPages();
  }

  #bindElements() {
    this.#chips = Array.from(this.querySelectorAll(SELECTORS.chip));
    this.#cards = Array.from(this.querySelectorAll(SELECTORS.card));
    this.#grid = this.querySelector(SELECTORS.grid);
    this.#emptyState = this.querySelector(SELECTORS.emptyState);
    this.#pagination = this.querySelector(SELECTORS.pagination);
    this.#sectionId = this.dataset.foSectionId || "";

    const parsedSize = parseInt(this.dataset.foPageSize, 10);
    this.#pageSize = parsedSize > 0 ? parsedSize : DEFAULT_PAGE_SIZE;

    if (this.#pagination) {
      this.#labels.previous = this.#pagination.dataset.foLabelPrevious || "Previous";
      this.#labels.next = this.#pagination.dataset.foLabelNext || "Next";
    }
  }

  #bindEvents() {
    this.addEventListener("click", this.#onClick.bind(this));
  }

  #onClick(event) {
    const chip = event.target.closest(SELECTORS.chip);
    if (chip) {
      this.setCategory(chip.dataset.foCategoryHandle || "");
      return;
    }

    const pageButton = event.target.closest(SELECTORS.pageButton);
    if (pageButton) {
      const page = parseInt(pageButton.dataset.foPage, 10);
      if (page) this.setPage(page);
    }
  }

  setCategory(handle) {
    this.#activeHandle = handle;
    this.#currentPage = 1; // Filterwechsel setzt die Seite zurück
    this.#render();
    this.#scrollToComponent();
  }

  setPage(page) {
    this.#currentPage = page;
    this.#render();
    this.#scrollToComponent(true);
  }

  #matchingCards() {
    if (!this.#activeHandle) return this.#cards;
    return this.#cards.filter(
      (card) => (card.dataset.foCategoryHandle || "") === this.#activeHandle
    );
  }

  #render({ updateUrl = true } = {}) {
    const filtered = this.#matchingCards();
    const totalPages = Math.max(1, Math.ceil(filtered.length / this.#pageSize));

    // Auf gültigen Bereich klemmen (z. B. wenn ein Filter die Trefferzahl senkt).
    this.#currentPage = Math.min(Math.max(1, this.#currentPage), totalPages);

    const start = (this.#currentPage - 1) * this.#pageSize;
    const pageSet = new Set(filtered.slice(start, start + this.#pageSize));

    // Sicht = filter(alle) -> slice(seite). Alles andere ausblenden.
    this.#cards.forEach((card) => {
      card.toggleAttribute("hidden", !pageSet.has(card));
    });

    this.#chips.forEach((chip) => {
      const isActive = (chip.dataset.foCategoryHandle || "") === this.#activeHandle;
      chip.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    if (this.#emptyState) {
      this.#emptyState.toggleAttribute("hidden", filtered.length !== 0);
    }

    this.#renderPagination(totalPages);

    if (updateUrl) this.#syncUrl();
  }

  #renderPagination(totalPages) {
    if (!this.#pagination) return;

    if (totalPages <= 1) {
      this.#pagination.setAttribute("hidden", "");
      this.#pagination.innerHTML = "";
      return;
    }

    const current = this.#currentPage;
    const parts = [];

    // Prev-Chevron
    parts.push(
      current > 1
        ? `<button type="button" data-fo-page="${current - 1}" class="${CLASS.link} ${CLASS.square}" aria-label="${this.#labels.previous}">&lsaquo;</button>`
        : `<span class="${CLASS.disabled} ${CLASS.square}" aria-hidden="true">&lsaquo;</span>`
    );

    // Seitenzahlen mit Fenster (±1) und "…"-Lücken
    const items = this.#pageWindow(totalPages)
      .map((entry) => {
        if (entry === "gap") {
          return `<li><span class="${CLASS.gap}">&hellip;</span></li>`;
        }
        if (entry === current) {
          return `<li><span class="${CLASS.active} ${CLASS.pill}" aria-current="page">${entry}</span></li>`;
        }
        return `<li><button type="button" data-fo-page="${entry}" class="${CLASS.link} ${CLASS.pill}">${entry}</button></li>`;
      })
      .join("");
    parts.push(`<ul class="flex items-center gap-0.5 sm:gap-1" role="list">${items}</ul>`);

    // Next-Chevron
    parts.push(
      current < totalPages
        ? `<button type="button" data-fo-page="${current + 1}" class="${CLASS.link} ${CLASS.square}" aria-label="${this.#labels.next}">&rsaquo;</button>`
        : `<span class="${CLASS.disabled} ${CLASS.square}" aria-hidden="true">&rsaquo;</span>`
    );

    this.#pagination.innerHTML = parts.join("");
    this.#pagination.removeAttribute("hidden");
  }

  #pageWindow(totalPages) {
    const current = this.#currentPage;
    const pages = new Set([1, totalPages]);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p >= 1 && p <= totalPages) pages.add(p);
    }

    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let previous = 0;
    for (const page of sorted) {
      if (page - previous > 1) result.push("gap");
      result.push(page);
      previous = page;
    }
    return result;
  }

  async #loadRemainingPages() {
    if (!this.#sectionId || !this.#grid) return;

    let hasNext = this.querySelector(SELECTORS.hasNext) !== null;
    let page = 2;

    while (hasNext && page <= MAX_FETCH_PAGES) {
      let result;
      try {
        result = await this.#fetchPageCards(page);
      } catch (error) {
        console.error("[FreebookOverview:loadRemainingPages]", error);
        break;
      }

      if (result.cards.length) {
        result.cards.forEach((card) => {
          const imported = document.importNode(card, true);
          this.#grid.appendChild(imported);
          this.#cards.push(imported);
        });
        // Neue Karten in die aktuelle Sicht (Filter + Seite) einbeziehen.
        this.#render({ updateUrl: false });
      }

      hasNext = result.hasNext;
      page += 1;
    }
  }

  async #fetchPageCards(page) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set("section_id", this.#sectionId);
    url.searchParams.set("page", String(page));

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Section render failed: ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    return {
      cards: Array.from(doc.querySelectorAll(SELECTORS.card)),
      hasNext: doc.querySelector(SELECTORS.hasNext) !== null,
    };
  }

  #syncUrl() {
    const url = new URL(window.location.href);

    if (this.#activeHandle) url.searchParams.set(CATEGORY_PARAM, this.#activeHandle);
    else url.searchParams.delete(CATEGORY_PARAM);

    if (this.#currentPage > 1) url.searchParams.set(PAGE_PARAM, String(this.#currentPage));
    else url.searchParams.delete(PAGE_PARAM);

    window.history.replaceState({}, "", url);
  }

  #scrollToComponent(force = false) {
    const rect = this.getBoundingClientRect();
    // Nur hochscrollen, wenn der Komponenten-Anfang bereits über dem
    // Viewport liegt (Seitenwechsel) — sonst kein Sprung beim Filtern am Kopf.
    if (!force && rect.top >= 0) return;
    window.scrollTo({ top: window.scrollY + rect.top - 80, behavior: "smooth" });
  }
}

if (!customElements.get("freebook-overview")) {
  customElements.define("freebook-overview", FreebookOverview);
}
